import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTemplate } from '@/contexts/TemplateContext'
import type { PublicProfile } from '@/types'
import { Gallery } from '@/components/gallery'
import { BookingModal } from '@/components/booking/BookingModal'

interface MakeupArtistTemplateProps {
    profile: PublicProfile
}

export function MakeupArtistTemplate({ profile }: MakeupArtistTemplateProps) {
    const { t } = useTranslation('public')
    const { colors, typography } = useTemplate()
    const [isBookingOpen, setIsBookingOpen] = useState(false)

    return (
        <div
            className="min-h-screen"
            style={{
                fontFamily: typography.body,
                backgroundColor: colors.colors.background,
                color: colors.colors.text,
            }}
        >
            {/* Navigation */}
            <nav
                className="sticky top-0 z-50 backdrop-blur-md"
                style={{ backgroundColor: `${colors.colors.primary}ee`, color: colors.colors.secondary }}
            >
                <div className="max-w-7xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            {profile.profile_image && (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="w-12 h-12 rounded-full object-cover border-2"
                                    style={{ borderColor: colors.colors.accent }}
                                />
                            )}
                            <div>
                                <h1 className="text-lg font-semibold tracking-wider">{profile.business_name}</h1>
                                {profile.tagline && (
                                    <p className="text-xs uppercase tracking-widest" style={{ color: colors.colors.accent }}>
                                        {profile.tagline}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="hidden md:flex items-center space-x-8">
                            <a href="#about" className="hover:text-gold transition-colors cursor-pointer" style={{ color: `${colors.colors.secondary}99` }}>
                                {t('template.beauty.about')}
                            </a>
                            <a href="#services" className="hover:text-gold transition-colors cursor-pointer" style={{ color: `${colors.colors.secondary}99` }}>
                                {t('template.beauty.services')}
                            </a>
                            <a href="#gallery" className="hover:text-gold transition-colors cursor-pointer" style={{ color: `${colors.colors.secondary}99` }}>
                                {t('template.beauty.portfolio')}
                            </a>
                            <a href="#contact" className="hover:text-gold transition-colors cursor-pointer" style={{ color: `${colors.colors.secondary}99` }}>
                                {t('template.beauty.contact')}
                            </a>
                            <a
                                onClick={() => setIsBookingOpen(true)}
                                className="px-6 py-2 rounded-full font-semibold hover:opacity-90 transition-colors cursor-pointer"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.primary }}
                            >
                                {t('template.beauty.bookNow')}
                            </a>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero - Dark with image */}
            <section
                className="relative min-h-screen flex items-center overflow-hidden"
                style={{ backgroundColor: colors.colors.primary }}
            >
                <div className="absolute inset-0">
                    {profile.cover_image && (
                        <img
                            src={profile.cover_image}
                            alt=""
                            className="w-full h-full object-cover"
                            style={{ opacity: 0.6 }}
                        />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
                </div>
                <div className="max-w-7xl mx-auto px-4 py-20 relative z-10">
                    <div className="max-w-2xl">
                        <p
                            className="uppercase tracking-widest mb-4 text-sm"
                            style={{ color: colors.colors.accent }}
                        >
                            Award-Winning Makeup Artist
                        </p>
                        <h1
                            className="text-5xl md:text-7xl font-bold leading-tight mb-6"
                            style={{ fontFamily: typography.heading, color: colors.colors.secondary }}
                        >
                            Enhancing Your{' '}
                            <span className="italic" style={{ color: colors.colors.accent }}>
                                Natural Beauty
                            </span>
                        </h1>
                        <p className="text-xl mb-8 leading-relaxed" style={{ color: `${colors.colors.secondary}cc` }}>
                            {profile.bio || 'Specializing in bridal, editorial, and special occasion makeup. Bringing luxury and artistry to every face I touch.'}
                        </p>
                        <div className="flex flex-wrap gap-4">
                            <a
                                href="#contact"
                                className="inline-flex items-center px-8 py-4 rounded-full font-semibold hover:opacity-90 transition-all cursor-pointer"
                                style={{ backgroundColor: colors.colors.accent, color: colors.colors.primary }}
                                >
                                    {t('template.beauty.bookAppointment')}
                                    <span className="ml-2">→</span>
                                </a>
                                <a
                                    href="#gallery"
                                    className="inline-flex items-center px-8 py-4 border-2 rounded-full font-semibold hover:opacity-90 transition-all cursor-pointer"
                                    style={{ borderColor: colors.colors.accent, color: colors.colors.accent }}
                                >
                                    {t('template.beauty.viewPortfolio')}
                                </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            <section id="about" className="py-24 px-4" style={{ backgroundColor: colors.colors.background }}>
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div className="relative">
                            {profile.profile_image && (
                                <img
                                    src={profile.profile_image}
                                    alt={profile.business_name}
                                    className="rounded-2xl shadow-2xl w-full object-cover"
                                />
                            )}
                            <div
                                className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                            >
                                <p className="text-4xl font-bold" style={{ color: colors.colors.accent }}>
                                    9+
                                </p>
                                <p className="text-sm">{t('template.beauty.yearsExperience')}</p>
                            </div>
                        </div>
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.aboutMe')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                The Art of{' '}
                                <span className="italic" style={{ color: colors.colors.accent }}>
                                    Elegance
                                </span>
                            </h2>
                            {profile.bio && (
                                <p className="text-lg mb-6 leading-relaxed" style={{ color: colors.colors.textMuted }}>
                                    {profile.bio}
                                </p>
                            )}
                            {profile.location && (
                                <p className="text-sm" style={{ color: colors.colors.textMuted }}>
                                    📍 {profile.location}
                                </p>
                            )}

                            {/* Social Links */}
                            {profile.social_links && profile.social_links.length > 0 && (
                                <div className="flex items-center space-x-4 mt-8">
                                    {profile.social_links.slice(0, 3).map((link) => (
                                        <a
                                            key={link.platform}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Follow on ${link.platform}`}
                                            className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                            style={{ backgroundColor: colors.colors.primary, color: colors.colors.accent }}
                                        >
                                            {link.platform === 'instagram' && '📷'}
                                            {link.platform === 'facebook' && '📘'}
                                            {link.platform === 'tiktok' && '🎵'}
                                            {link.platform === 'pinterest' && '📌'}
                                            {link.platform === 'youtube' && '📺'}
                                            {link.platform === 'twitter' && '🐦'}
                                            {!['instagram', 'facebook', 'tiktok', 'pinterest', 'youtube', 'twitter'].includes(link.platform) && '🔗'}
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Services */}
            {profile.services && profile.services.length > 0 && (
                <section
                    id="services"
                    className="py-24 px-4"
                    style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                >
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                                >
                                    {t('template.beauty.services')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.pricingAndServices')}
                                </h2>
                        </div>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {profile.services.map((service) => (
                                <div
                                    key={service.id}
                                    className="rounded-2xl p-8 transition-colors border"
                                    style={{
                                        backgroundColor: colors.colors.textMuted,
                                        borderColor: `${colors.colors.accent}33`,
                                    }}
                                >
                                    <div className="flex justify-between items-start mb-6">
                                        <div
                                            className="w-16 h-16 rounded-full flex items-center justify-center"
                                            style={{ backgroundColor: `${colors.colors.accent}1a` }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>✨</span>
                                        </div>
                                        {service.price_display !== 'hidden' && service.price && (
                                            <span className="text-3xl font-bold" style={{ color: colors.colors.accent }}>
                                                {service.price_display === 'from' && 'From '}${service.price.toFixed(0)}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="text-2xl font-semibold mb-3">{service.name}</h3>
                                    {service.description && (
                                        <p className="mb-4" style={{ color: `${colors.colors.secondary}99` }}>
                                            {service.description}
                                        </p>
                                    )}
                                    {service.duration_minutes && (
                                        <div className="flex items-center text-sm" style={{ color: colors.colors.accent }}>
                                            <span className="mr-2">⏱</span>
                                            <span>{service.duration_minutes} {t('template.common.min')}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Gallery */}
            {profile.gallery && profile.gallery.length > 0 && (
                <section id="gallery" className="py-24 px-4" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-7xl mx-auto">
                        <div className="text-center mb-16">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                                >
                                    {t('template.beauty.portfolio')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.myWork')}
                                </h2>
                        </div>
                        <Gallery
                            images={profile.gallery}
                            layout="grid"
                            limit={8}
                            enableLightbox={true}
                            showCaptions={true}
                            aspectRatio="square"
                            gapSize={4}
                        />
                    </div>
                </section>
            )}

            {/* Hours */}
            {profile.availability && profile.availability.length > 0 && (
                <section className="py-24 px-4" style={{ backgroundColor: colors.colors.background }}>
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-12">
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                                >
                                    {t('template.beauty.availability')}
                                </p>
                                <h2
                                    className="text-4xl md:text-5xl font-bold"
                                    style={{ fontFamily: typography.heading }}
                                >
                                    {t('template.beauty.studioHours')}
                                </h2>
                        </div>
                        <div
                            className="rounded-3xl p-8 md:p-12"
                            style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
                        >
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    {profile.availability.slice(0, 4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.textMuted }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span style={{ color: `${colors.colors.secondary}99` }}>
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.byAppointment')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="space-y-4">
                                    {profile.availability.slice(4).map((day) => (
                                        <div
                                            key={day.day_of_week}
                                            className="flex justify-between items-center py-3 border-b"
                                            style={{ borderColor: colors.colors.textMuted }}
                                        >
                                            <span className="font-semibold">{day.day_name}</span>
                                            <span
                                                style={{
                                                    color: day.is_available ? `${colors.colors.secondary}99` : colors.colors.accent,
                                                }}
                                            >
                                                {day.is_available
                                                    ? day.is_all_day
                                                        ? t('template.common.allDay')
                                                        : `${day.start_time} - ${day.end_time}`
                                                    : t('template.common.byAppointment')}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Contact */}
            <section
                id="contact"
                className="py-24 px-4"
                style={{ backgroundColor: colors.colors.primary, color: colors.colors.secondary }}
            >
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16">
                        <div>
                            <p
                                className="uppercase tracking-widest mb-4 text-sm"
                                style={{ color: colors.colors.accent }}
                            >
                                {t('template.beauty.contact')}
                            </p>
                            <h2
                                className="text-4xl md:text-5xl font-bold mb-6"
                                style={{ fontFamily: typography.heading }}
                            >
                                Let's Create{' '}
                                <span className="italic" style={{ color: colors.colors.accent }}>
                                    Magic
                                </span>
                            </h2>
                            <p className="text-lg mb-8 leading-relaxed" style={{ color: `${colors.colors.secondary}99` }}>
                                Ready to look and feel your absolute best? Reach out to book your appointment or inquire about my services.
                            </p>
                            <div className="space-y-6">
                                {profile.location && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.accent}1a` }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📍</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold mb-1">{t('template.beauty.studioLocation')}</h4>
                                            <p style={{ color: `${colors.colors.secondary}99` }}>{profile.location}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.phone && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.accent}1a` }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>📞</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold mb-1">{t('template.beauty.phone')}</h4>
                                            <p style={{ color: `${colors.colors.secondary}99` }}>{profile.phone}</p>
                                        </div>
                                    </div>
                                )}
                                {profile.email && (
                                    <div className="flex items-start space-x-4">
                                        <div
                                            className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                                            style={{ backgroundColor: `${colors.colors.accent}1a` }}
                                        >
                                            <span style={{ color: colors.colors.accent }}>✉️</span>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold mb-1">{t('template.beauty.email')}</h4>
                                            <p style={{ color: `${colors.colors.secondary}99` }}>{profile.email}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contact CTA */}
                        <div className="flex flex-col justify-center">
                            <div className="flex flex-col sm:flex-row gap-3">
                                {profile.phone && (
                                    <a
                                        href={`tel:${profile.phone}`}
                                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-lg transition-opacity hover:opacity-90"
                                        style={{
                                            backgroundColor: colors.colors.accent,
                                            color: colors.colors.primary,
                                        }}
                                    >
                                        📞 {t('template.beauty.callNow')}
                                    </a>
                                )}
                                {profile.email && (
                                    <a
                                        href={`mailto:${profile.email}`}
                                        className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-bold text-lg transition-opacity hover:opacity-90"
                                        style={{
                                            backgroundColor: colors.colors.textMuted,
                                            color: colors.colors.primary,
                                        }}
                                    >
                                        ✉️ {t('template.beauty.emailMe')}
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer
                className="py-12 px-4 text-center border-t"
                style={{ backgroundColor: colors.colors.primary, borderColor: colors.colors.textMuted }}
            >
                {profile.profile_image && (
                    <img
                        src={profile.profile_image}
                        alt={profile.business_name}
                        className="w-16 h-16 rounded-full mx-auto mb-4 border-2"
                        style={{ borderColor: colors.colors.accent }}
                    />
                )}
                <h3 className="text-xl font-semibold tracking-wider mb-2">{profile.business_name}</h3>
                {profile.tagline && (
                    <p className="text-sm uppercase tracking-widest mb-6" style={{ color: colors.colors.accent }}>
                        {profile.tagline}
                    </p>
                )}
                {profile.social_links && profile.social_links.length > 0 && (
                    <div className="flex justify-center space-x-4 mb-6">
                        {profile.social_links.slice(0, 3).map((link) => (
                            <a
                                key={link.platform}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Follow on ${link.platform}`}
                                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80"
                                style={{ backgroundColor: colors.colors.textMuted, color: colors.colors.accent }}
                            >
                                {link.platform === 'instagram' && '📷'}
                                {link.platform === 'facebook' && '📘'}
                                {link.platform === 'tiktok' && '🎵'}
                                {link.platform === 'pinterest' && '📌'}
                                {!['instagram', 'facebook', 'tiktok', 'pinterest'].includes(link.platform) && '🔗'}
                            </a>
                        ))}
                    </div>
                )}
                <p className="text-sm" style={{ color: `${colors.colors.secondary}66` }}>
                    © {new Date().getFullYear()} {profile.business_name}. {t('template.beauty.poweredBy')}
                </p>
            </footer>

            {isBookingOpen && profile.services && profile.services.length > 0 && (
                <BookingModal
                    isOpen={isBookingOpen}
                    onClose={() => setIsBookingOpen(false)}
                    profile={profile}
                    services={profile.services}
                    categories={[]}
                />
            )}
        </div>
    )
}

export default MakeupArtistTemplate;
